/**
 * Loads a single block definition's geometry for the inspector.
 * Only the manifest plus the selected definition's .bin is fetched; opened
 * definitions are cached in-memory for instant switching.
 */
import { API } from "@/lib/api";
import { buildGeometries } from "@/viewer-core/assetLoader";

const manifests = new Map();
const definitions = new Map();

function base(projectId, revisionId) {
  return `${API}/projects/${projectId}/models/${revisionId}/assets`;
}

export async function loadInspectorManifest(projectId, revisionId) {
  const key = `${projectId}:${revisionId}`;
  if (manifests.has(key)) return manifests.get(key);
  const res = await fetch(`${base(projectId, revisionId)}/manifest.json`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to load model manifest (${res.status})`);
  const manifest = await res.json();
  manifests.set(key, manifest);
  return manifest;
}

/** Returns { parts, tree, dimensions } for one definition (cached). */
export async function loadDefinition(projectId, revisionId, definition) {
  const key = `${projectId}:${revisionId}:${definition.index}`;
  if (definitions.has(key)) return definitions.get(key);
  const res = await fetch(`${base(projectId, revisionId)}/${definition.file}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to load geometry (${res.status})`);
  const buffer = await res.arrayBuffer();
  const built = buildGeometries(buffer, definition);
  const parts = built.map((part, index) => ({
    ...part,
    node: definition.parts[index]?.node || "",
    vertexCount: definition.parts[index]?.vertexCount || 0,
  }));
  const payload = { parts, tree: definition.tree || [] };
  definitions.set(key, payload);
  return payload;
}

/** Releases every cached definition geometry (called when the page unmounts). */
export function clearInspectorCache() {
  manifests.clear();
  definitions.forEach(({ parts }) =>
    parts.forEach((part) => part.geometry?.dispose?.())
  );
  definitions.clear();
}
